/**
 * The workbook domain model and the roll-up arithmetic.
 *
 * `buildModel` is a behaviour-preserving port of the same function in
 * floorplan_generator_v2.html. It is the SINGLE place percentages are
 * calculated (CLAUDE.md), imported by both the API and the browser so a
 * server-computed figure and a browser-computed one cannot drift apart.
 *
 * Two rounding behaviours are load-bearing and deliberately preserved even
 * though they look like defects:
 *
 *   1. A room's percentage is rounded BEFORE it is weighted into the overall
 *      figure, so the overall figure is a weighted average of rounded values.
 *   2. Pillars with a zero weighting contribute nothing to their room — not
 *      even their deliverables — because both the numerator and denominator
 *      are guarded by `if (weighting)`.
 *
 * Changing either would move published percentages. Do not "fix" them without
 * a decision recorded in design.md.
 */
export const STATE_ORDER = ['Draft', 'Manuscript', 'Proof', 'Published'];
/**
 * The generator's hardcoded ramp, as fractions of 1. Retained because the frozen
 * generator still uses it and the golden fixtures were recorded against it.
 *
 * It is NO LONGER what buildModel uses: the scale is per-version data from
 * Meta (design.md § 7.9). See STATE_SCALE_LEGACY for the same ramp expressed on
 * the 0-100 scale buildModel now takes.
 */
export const STATE_VALUE = {
    Draft: 0,
    Manuscript: 1 / 3,
    Proof: 2 / 3,
    Published: 1,
};
/** The default when a version's Meta says nothing — design.md § 7.9. */
export const STATE_SCALE_DEFAULT = {
    Draft: 0,
    Manuscript: 40,
    Proof: 90,
    Published: 100,
};
/**
 * The generator's 0 / 33 1/3 / 66 2/3 / 100 ramp, for versions published before
 * the scale became data. This is the exact ramp backfilled into the existing
 * published v1, and the value buildModel reproduces bit-for-bit against the
 * golden fixtures (see the note on scaleFraction below).
 */
export const STATE_SCALE_LEGACY = {
    Draft: 0,
    Manuscript: 100 / 3,
    Proof: 200 / 3,
    Published: 100,
};
export const STATE_CLASS = {
    Draft: 's-draft',
    Manuscript: 's-manuscript',
    Proof: 's-proof',
    Published: 's-published',
};
/** Canonical keys used in the database and the API. The workbook uses the labels above. */
export const STATE_KEY = {
    Draft: 'draft',
    Manuscript: 'manuscript',
    Proof: 'proof',
    Published: 'published',
};
export function isDeliverableState(v) {
    return typeof v === 'string' && STATE_ORDER.includes(v);
}
/* ------------------------------------------------------------------- owner */
/** Who a deliverable belongs to — us or the client. design.md § 7.6. */
export const OWNER_VALUES = ['consultant', 'client'];
/** What a deliverable is when the workbook does not say. */
export const DEFAULT_OWNER = 'consultant';
/** The words accepted in the optional `Owner` column, normalised. */
const OWNER_BY_WORD = {
    consultant: 'consultant',
    us: 'consultant',
    client: 'client',
};
/**
 * Resolve an `Owner` cell. Trimmed and case-insensitive, matching how
 * parseStateCell normalises, because the same people type into the same sheet.
 *
 * Three outcomes, deliberately distinguished:
 *   - blank, whitespace, absent  -> 'consultant', silently. The column is
 *     optional, so every pre-7.6 workbook imports unchanged.
 *   - a recognised word          -> that owner.
 *   - present but unrecognised   -> null, so the CALLER falls back to
 *     'consultant' AND warns. Swallowing a word someone actually typed is how a
 *     plan quietly reports the wrong consultant/client split.
 */
export function parseOwnerCell(raw) {
    if (raw === undefined || raw === null)
        return DEFAULT_OWNER;
    const norm = String(raw).trim().toLowerCase();
    if (!norm)
        return DEFAULT_OWNER;
    return OWNER_BY_WORD[norm] ?? null;
}
/**
 * The owner of a row, never null — an unrecognised word reads as 'consultant',
 * which buildModel has already warned about.
 *
 * Deliberately NOT stamped onto the row by buildModel the way `__i` is: these
 * rows are also the API's response shape, so a field invented here would have
 * to be added to the wire schema in the same breath. Callers that need the
 * owner (the two-bar timeline, § 7.7) call this; it stays the one place the
 * word is interpreted.
 */
export function ownerOf(d) {
    return parseOwnerCell(d.Owner) ?? DEFAULT_OWNER;
}
/** What a deliverable's hours are when the workbook does not say. */
export const DEFAULT_HOURS = null;
/**
 * The largest value `deliverable.hours numeric(10,2)` can hold.
 *
 * This is the column's own ceiling, not a judgement about how long a
 * deliverable could plausibly take — "a hundred million hours is too many"
 * would be an opinion about somebody's estimates and is not ours to hold. "This
 * column cannot store that number" is a fact about the system, the same kind of
 * fact as "this is not a number at all", so the bound is the storage bound
 * exactly rather than a rounder figure that looks more like an hours value.
 *
 * It is enforced HERE rather than left to the database because of the failure
 * mode. Every other unusable cell in § 10b costs one deliverable its hours and
 * says so; an unbounded parse does not fail the cell, it fails the UPLOAD — one
 * stray 1e9 in a 594-row workbook aborts the whole import on a numeric overflow
 * and tells nobody which row caused it.
 */
export const MAX_HOURS = 99999999.99;
/**
 * The single reading of an `Hours` cell: a value, nothing, or a problem.
 *
 * Unexported — `parseHoursCell` is the public shape and buildModel uses this
 * one only to tell the two warnings apart. One function so there is one set of
 * rules; "larger than the column holds" and "not a number" must never be able
 * to disagree about what counts as a number.
 */
function readHoursCell(raw) {
    if (raw === undefined || raw === null)
        return { hours: DEFAULT_HOURS };
    // Booleans and dates are unusable, not numbers: `Number(true)` is 1 and a
    // Date coerces to a millisecond count, either of which invents an estimate.
    if (typeof raw === 'boolean' || raw instanceof Date)
        return { problem: 'unusable' };
    let n;
    if (typeof raw === 'number')
        n = raw;
    else if (typeof raw !== 'string')
        return { problem: 'unusable' };
    else {
        const norm = raw.trim();
        if (!norm)
            return { hours: DEFAULT_HOURS };
        n = Number(norm);
    }
    if (!Number.isFinite(n) || n < 0)
        return { problem: 'unusable' };
    if (n > MAX_HOURS)
        return { problem: 'too-large' };
    return { hours: n };
}
/**
 * Resolve an `Hours` cell. Three outcomes, deliberately distinguished — the
 * same shape as parseOwnerCell, because the same argument applies:
 *
 *   - blank, whitespace, absent  -> null, SILENTLY. The column is optional, so
 *     every pre-10b workbook imports unchanged, and most rows of a workbook
 *     that has it are blank too.
 *   - a non-negative number      -> that number. Integer or fractional: effort
 *     is naturally fractional and someone will type 7.5.
 *   - negative, non-numeric, or  -> `undefined`, so the CALLER stores no hours
 *     larger than MAX_HOURS         AND warns. Swallowing a number somebody
 *     typed is how a plan quietly reports the wrong effort.
 */
export function parseHoursCell(raw) {
    const read = readHoursCell(raw);
    return 'problem' in read ? undefined : read.hours;
}
/**
 * The hours of a row, never `undefined` — an unusable cell reads as "not
 * estimated", which buildModel has already warned about.
 *
 * Deliberately NOT folded into any percentage. Hours are stored and given back;
 * buildModel's arithmetic is unchanged (design.md § 10b).
 */
export function hoursOf(d) {
    const parsed = parseHoursCell(d.Hours);
    return parsed === undefined ? DEFAULT_HOURS : parsed;
}
const num = (v) => Number(v) || 0;
/**
 * A scale entry (percentage points) as the fraction of 1 the roll-up works in.
 *
 * Why this is not simply `pct / 100`. The generator's ramp uses the literals
 * `1/3` and `2/3`, and `(100/3)/100` is NOT the same double as `1/3` — it is one
 * unit in the last place larger. Nearly always that is invisible, because the
 * result is rounded to a whole percent. It is not invisible when a room's
 * figure lands exactly on a .5 boundary: `1/3` sits just BELOW a true third, so
 * the generator rounds such a room down, while `(100/3)/100` sits just above and
 * rounds it up. A brute-force search over two-pillar rooms found 22,276 such
 * cases in 11.2 million, so this is reachable, not theoretical. The real plan
 * happens not to contain one (test/golden/build-model.test.ts), which is luck
 * and not a guarantee for the next workbook published on the legacy ramp.
 *
 * So: a scale entry that is indistinguishable from the generator's ramp IS the
 * generator's ramp, and uses its exact constant. The tolerance matters because
 * 33 1/3 has no exact decimal form — a backfill, a JSON round trip or a person
 * typing 33.333333333 all mean the same third.
 *
 * Any other value converts normally. Both the API and the browser run this same
 * function, so a value like 40 that is itself inexact in binary cannot drift
 * between them.
 */
const THIRD_TOLERANCE = 1e-9;
const scaleFraction = (pct, state) => {
    const v = Number(pct) || 0;
    const generator = STATE_VALUE[state];
    return Math.abs(v - generator * 100) < THIRD_TOLERANCE ? generator : v / 100;
};
export const DEFAULT_WARNING_TERMS = {
    room: { singular: 'Room', plural: 'Rooms' },
    pillar: { singular: 'Pillar', plural: 'Pillars' },
    deliverable: { singular: 'Deliverable', plural: 'Deliverables' },
    draftState: 'Draft',
};
const lower = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
/**
 * Roll deliverables up to pillars, pillars to rooms, rooms to the overall figure.
 * Deliverables count equally within their pillar; slivers carry no weight.
 */
export function buildModel(meta, rooms, pillars, deliverables, terms = DEFAULT_WARNING_TERMS, scale = STATE_SCALE_DEFAULT) {
    const warnings = [];
    const T = terms;
    deliverables.forEach((d, i) => {
        d.__i = i;
    });
    const sortedRooms = rooms.slice().sort((a, b) => num(a.Order) - num(b.Order));
    const pillarsByRoom = new Map();
    for (const p of pillars) {
        const list = pillarsByRoom.get(p.RoomID);
        if (list)
            list.push(p);
        else
            pillarsByRoom.set(p.RoomID, [p]);
    }
    const byRoomPillar = new Map();
    for (const d of deliverables) {
        const key = `${d.RoomID}||${d.PillarName}`;
        const list = byRoomPillar.get(key);
        if (list)
            list.push(d);
        else
            byRoomPillar.set(key, [d]);
        if (!isDeliverableState(d.State)) {
            warnings.push(`${T.deliverable.singular} "${d.DeliverableName}" (${lower(T.room.singular)} ${d.RoomID}) has an unrecognised state "${d.State}" — treated as ${T.draftState}.`);
        }
        if (parseOwnerCell(d.Owner) === null) {
            warnings.push(`${T.deliverable.singular} "${d.DeliverableName}" (${lower(T.room.singular)} ${d.RoomID}) has an unrecognised owner "${String(d.Owner).trim()}" — treated as Consultant.`);
        }
        // Two warnings, not one: "is larger than can be stored" and "is not a
        // number" are different mistakes, and the person who typed it needs to know
        // which one they made.
        const hoursRead = readHoursCell(d.Hours);
        if ('problem' in hoursRead) {
            const cell = String(d.Hours).trim();
            const subject = `${T.deliverable.singular} "${d.DeliverableName}" (${lower(T.room.singular)} ${d.RoomID})`;
            warnings.push(hoursRead.problem === 'too-large'
                ? `${subject} has an Hours value "${cell}" larger than the largest value that can be stored (${MAX_HOURS}) — recorded as no hours.`
                : `${subject} has an unusable Hours value "${cell}" — recorded as no hours.`);
        }
    }
    const roomModels = sortedRooms.map((r) => {
        const rPillars = pillarsByRoom.get(r.RoomID) ?? [];
        const weightSum = rPillars.reduce((s, p) => s + num(p.Weighting), 0);
        if (rPillars.length && Math.abs(weightSum - 1) > 0.02) {
            warnings.push(`${T.room.singular} "${r.RoomName}" ${lower(T.pillar.singular)} weightings sum to ${(weightSum * 100).toFixed(0)}%, not 100%.`);
        }
        let weightedScore = 0;
        let weightTotal = 0;
        const pillarModels = rPillars.map((pRow) => {
            const pname = pRow.PillarName;
            const weighting = num(pRow.Weighting);
            const dels = byRoomPillar.get(`${r.RoomID}||${pname}`) ?? [];
            const hasOverride = pRow.PercentOverride !== undefined &&
                pRow.PercentOverride !== null &&
                pRow.PercentOverride !== '';
            const overrideAvg = hasOverride ? Number(pRow.PercentOverride) : null;
            const values = dels.map((d) => isDeliverableState(d.State) ? scaleFraction(scale[d.State], d.State) : 0);
            const computedAvg = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
            const avg = hasOverride ? overrideAvg : computedAvg;
            // Guarded by `weighting` in the original: a zero-weighted pillar is
            // excluded from both numerator and denominator. Preserved deliberately.
            if (weighting) {
                weightedScore += avg * weighting;
                weightTotal += weighting;
            }
            // Sliver sub-groups, preserving sheet order. A blank SliverName means the
            // deliverable sits directly on the pillar. Consecutive rows with the same
            // sliver merge; a repeated sliver later in the sheet starts a new group.
            const groups = [];
            for (const d of dels) {
                const sname = d.SliverName === undefined || d.SliverName === null ? '' : String(d.SliverName).trim();
                const last = groups[groups.length - 1];
                if (last && last.sliver === sname)
                    last.deliverables.push(d);
                else
                    groups.push({ sliver: sname, deliverables: [d] });
            }
            return {
                name: pname,
                weighting,
                groups,
                deliverableCount: dels.length,
                isOverride: hasOverride && !dels.length,
                overridePct: hasOverride ? Math.round(overrideAvg * 100) : null,
            };
        });
        if (!rPillars.length)
            warnings.push(`${T.room.singular} "${r.RoomName}" has no ${lower(T.pillar.plural)} defined.`);
        const roomPct = weightTotal ? Math.round((weightedScore / weightTotal) * 100) : 0;
        return { ...r, pillars: pillarModels, pct: roomPct, weight: num(r.Weighting) };
    });
    const totalWeight = roomModels.reduce((s, r) => s + r.weight, 0);
    let overallPct;
    let weightNote = '';
    if (totalWeight > 0) {
        if (Math.abs(totalWeight - 1) > 0.01) {
            warnings.push(`${T.room.singular} weightings sum to ${(totalWeight * 100).toFixed(1)}%, not 100% — the overall figure is normalised by the actual total.`);
        }
        // Weighted average of ALREADY ROUNDED room percentages. See the header note.
        overallPct = Math.round(roomModels.reduce((s, r) => s + r.pct * r.weight, 0) / totalWeight);
    }
    else {
        overallPct = roomModels.length
            ? Math.round(roomModels.reduce((s, r) => s + r.pct, 0) / roomModels.length)
            : 0;
        weightNote = 'unweighted average — no room weightings found';
        warnings.push(`No ${lower(T.room.singular)} weightings found on the Rooms sheet — the overall figure is a plain average of ${lower(T.room.singular)} percentages.`);
    }
    return {
        meta,
        rooms: roomModels,
        overallPct,
        weightNote,
        deliverableCount: deliverables.length,
        warnings,
        raw: { meta, rooms, pillars, deliverables },
    };
}
//# sourceMappingURL=model.js.map